import { useAuth } from "@/contexts/AuthContext";
import api from "@shared/api/client";
import type { CreateBankRequest } from "@shared/types/settings";
import { BankType } from "@shared/types/settings";
import { Step, type SetupWizardContextValue, type WizardState } from "@shared/types/setup";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const stepOrder: readonly Step[] = [Step.Bank, Step.Address, Step.Provider] as const;

const initialState: WizardState = {
  currentStep: Step.Bank,
  loading: false,
  error: null,
  success: false,
  providers: [],
  selectedAddressId: null,
  bankName: "",
  bankType: BankType.ASA,
  bankUsername: "",
  bankPassword: "",
  addressName: "",
  selectedProviderId: "",
  providerUsername: "",
  providerPassword: "",
  providerSkipPayment: false,
  providerTaxDeduction: false,
  providerTaxDeductionAmount: "",
};

const useSetupWizard = () => {
  const { t } = useTranslation("setup");
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [state, _setState] = useState<WizardState>(initialState);

  const setState = useCallback((partial: Partial<WizardState> | ((prev: WizardState) => Partial<WizardState>)) => {
    _setState((s) => {
      const next = typeof partial === "function" ? partial(s) : partial;
      return { ...s, ...next };
    });
  }, []);

  const checkSetupStatus = useCallback(async () => {
    if (!user) return;
    try {
      const status = await api.setup.getStatus();
      if (status.hasSetupComplete) {
        navigate("/dashboard");
      }
    } catch (err: unknown) {
      console.error("Failed to check setup status", err);
    }
  }, [user, navigate]);

  const loadProviders = useCallback(async () => {
    try {
      const data = await api.providers.list();
      setState((s) => ({ ...s, providers: data }));
    } catch (err: unknown) {
      console.error("Failed to load providers", err);
    }
  }, []);

  useEffect(() => {
    if (state.currentStep === Step.Provider) {
      void loadProviders();
    }
  }, [state.currentStep, loadProviders]);

  const handleBankSubmit = useCallback(async () => {
    if (!state.bankName.trim()) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data: CreateBankRequest = {
        name: state.bankName.trim(),
        type: state.bankType,
        username: state.bankUsername || undefined,
        password: state.bankPassword || undefined,
      };
      await api.banks.create(data);
      setState((s) => ({ ...s, currentStep: Step.Address, loading: false }));
    } catch (err: unknown) {
      setState((s) => ({ ...s, error: err instanceof Error ? err.message : t("createBankError"), loading: false }));
    }
  }, [state.bankName, state.bankType, state.bankUsername, state.bankPassword, t]);

  const handleAddressSubmit = useCallback(async () => {
    if (!state.addressName.trim()) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const created = await api.addresses.create(state.addressName.trim());
      setState((s) => ({ ...s, selectedAddressId: created.id, currentStep: Step.Provider, loading: false }));
    } catch (err: unknown) {
      setState((s) => ({ ...s, error: err instanceof Error ? err.message : t("createAddressError"), loading: false }));
    }
  }, [state.addressName, t]);

  const handleProviderSubmit = useCallback(async () => {
    if (!state.selectedAddressId || !state.selectedProviderId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await api.addresses.assignProvider(state.selectedAddressId, {
        providerId: parseInt(state.selectedProviderId, 10),
        username: state.providerUsername || undefined,
        password: state.providerPassword || undefined,
        skipPayment: state.providerSkipPayment,
        taxDeduction: state.providerTaxDeduction,
        taxDeductionAmount: state.providerTaxDeduction ? parseFloat(state.providerTaxDeductionAmount) || 0 : undefined,
      });
      await api.setup.complete();
      await refreshUser();
      setState((s) => ({ ...s, success: true, loading: false }));
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err: unknown) {
      setState((s) => ({ ...s, error: err instanceof Error ? err.message : t("assignProviderError"), loading: false }));
    }
  }, [
    state.selectedAddressId,
    state.selectedProviderId,
    state.providerUsername,
    state.providerPassword,
    state.providerSkipPayment,
    state.providerTaxDeduction,
    state.providerTaxDeductionAmount,
    t,
    navigate,
    refreshUser,
  ]);

  const handleSkip = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await api.setup.complete();
      await refreshUser();
      setState((s) => ({ ...s, success: true, loading: false }));
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err: unknown) {
      setState((s) => ({ ...s, error: err instanceof Error ? err.message : t("completeSetupError"), loading: false }));
    }
  }, [t, navigate, refreshUser]);

  const contextValue = useMemo(
    () => ({
      ...state,
      setState,
      setCurrentStep: (step: Step) => setState((s) => ({ ...s, currentStep: step })),
      handleBankSubmit,
      handleAddressSubmit,
      handleProviderSubmit,
      handleSkip,
      t,
      checkSetupStatus,
      stepOrder,
    }),
    [state, setState, handleBankSubmit, handleAddressSubmit, handleProviderSubmit, handleSkip, t, checkSetupStatus],
  );

  return { contextValue };
};

const SetupWizardContext = createContext<SetupWizardContextValue | null>(null);

const useStepContext = () => {
  const context = useContext(SetupWizardContext);
  if (context === null) {
    throw new Error("useStepContext must be used within a SetupWizardProvider");
  }
  return context;
};

export { useSetupWizard, useStepContext, stepOrder, SetupWizardContext };
